import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  Alert,
  Anchor,
  Avatar,
  Button,
  Card,
  Divider,
  Heading,
  List,
  MediaObject,
  Paragraph,
  Text,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { LoadingButton, LoadingHeading, LoadingText } from '@zg-rentals/loading-components';

import IdentityContentBox from '../../components/Widgets/IdentityContentBox';
import { FULLSTORY_EXCLUDE } from '../../constants/analytics';
import { ZILLOW_LEASES_EMAIL, ZILLOW_RENTER_TERMS_OF_USE, ZILLOW_TERMS_OF_USE } from '../../constants/stringConstants';
import { LeaseTypes } from '../../models/Types';
import { useAssociateUserMutation, useInfoForUser, useLeaseFromOuidQuery, useRenterUser } from '../../utils/queries';
import NoAccessLeaseAlert from '../../components/Widgets/NoAccessLeaseAlert';
import { emitClickEmailViewLeaseEvent } from '../../../analytics/events';

export function TenantWelcome() {
  const [isUserEmailMismatched, setIsUserEmailMismatched] = useState(true);

  const queryParameters = new URLSearchParams(window.location.search);
  const ouid = queryParameters.get('ouid');
  const infoForUserQuery = useInfoForUser(ouid, !!ouid);
  const leaseFromOuidQuery = useLeaseFromOuidQuery(ouid, !!ouid);
  const renterSignedInUserQuery = useRenterUser();
  const associateUserMutation = useAssociateUserMutation();
  const { enqueueToast } = useToast();

  const onAssociateUserErrorToast = useCallback(() => {
    enqueueToast(
      <Toast
        tone="critical"
        actionButton={
          <TextButton asChild onImpact>
            <a href={`mailto:${ZILLOW_LEASES_EMAIL}`}>Contact</a>
          </TextButton>
        }
      >
        There’s an issue with your account — please contact customer care to view your lease
      </Toast>,
    );
  }, [enqueueToast]);

  useEffect(() => {
    if (associateUserMutation.isError) {
      onAssociateUserErrorToast();
    }
  }, [associateUserMutation.isError, onAssociateUserErrorToast]);

  const history = useHistory();

  useEffect(() => {
    if (infoForUserQuery?.data?.email?.toLowerCase() && renterSignedInUserQuery?.data?.email?.toLowerCase()) {
      if (infoForUserQuery?.data?.email?.toLowerCase() === renterSignedInUserQuery?.data?.email?.toLowerCase()) {
        setIsUserEmailMismatched(false);
      } else {
        setIsUserEmailMismatched(true);
      }
    }
  }, [infoForUserQuery?.data, renterSignedInUserQuery?.data]);

  async function onViewLease() {
    const utmMedium = queryParameters?.get('utm_medium');

    if (utmMedium === 'email') {
      try {
        emitClickEmailViewLeaseEvent({
          lease: { ...leaseFromOuidQuery.data, id: Number(leaseFromOuidQuery.data?.id) },
          user: renterSignedInUserQuery.data,
          listing: { listingAlias: leaseFromOuidQuery.data?.listingAlias },
        });
      } catch (e) {
        // Ignore. Fails when user is not logged in.
      }
    }

    await associateUserMutation.mutateAsync({
      getParams: {
        ouid,
      },
    });

    history.push(`${leaseFromOuidQuery?.data?.getSummaryPath()}${window.location.search}`);
  }

  function renderIdentityContentBoxChildren(): React.ReactNode {
    return (
      <>
        <Paragraph css={{ textStyle: 'body-sm', marginBottom: 'tighter' }}>
          By <strong>continuing to view your lease</strong>, you agree to comply with the{' '}
          <Anchor target="_blank" rel="noopener noreferrer" href={ZILLOW_TERMS_OF_USE}>
            Terms of Use
          </Anchor>{' '}
          and the{' '}
          <Anchor target="_blank" rel="noopener noreferrer" href={ZILLOW_RENTER_TERMS_OF_USE}>
            Rental User Terms
          </Anchor>
        </Paragraph>
        <Button
          tone="brand"
          emphasis="filled"
          fluid
          disabled={infoForUserQuery?.isLoading || isUserEmailMismatched}
          onClick={onViewLease}
          data-test="tenant-landing-page-view-lease"
        >
          Continue to view your lease
        </Button>
      </>
    );
  }

  // Show loading state while queries are fetching
  const isLoading = infoForUserQuery.isLoading || leaseFromOuidQuery.isLoading || renterSignedInUserQuery.isLoading;

  if (isLoading) {
    return (
      <>
        <LoadingHeading
          isLoading
          level={2}
          loadingWidth="60%"
          css={{
            textStyle:
              'responsive.heading-xs' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
            marginBottom: 'tighter',
          }}
        >
          Loading
        </LoadingHeading>
        <LoadingText isLoading loadingWidth="30%" css={{ wordBreak: 'break-word' }}>
          Loading
        </LoadingText>
        <LoadingText isLoading loadingWidth="10%" css={{ marginBottom: 'tight' }}>
          Loading
        </LoadingText>
        <LoadingText isLoading loadingWidth="80%" css={{ marginBottom: 'tight' }}>
          Loading
        </LoadingText>
        <Card tone="neutral" elevated={false} css={{ marginBottom: 'tight', gap: 'tight' }}>
          <LoadingHeading
            isLoading
            level={3}
            loadingWidth="50%"
            css={{
              textStyle:
                'responsive.heading-sm' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
            }}
          >
            Loading
          </LoadingHeading>
          <LoadingText isLoading loadingWidth="90%" css={{ marginBottom: 'tighter' }}>
            Loading
          </LoadingText>
          <LoadingText isLoading loadingWidth="95%" css={{ marginBottom: 'tighter' }}>
            Loading
          </LoadingText>
          <LoadingText isLoading loadingWidth="100%">
            Loading
          </LoadingText>
        </Card>
        <MediaObject media={<Avatar size="lg" />}>
          <LoadingText isLoading loadingWidth="60%" css={{ marginBottom: 'tight' }}>
            Loading
          </LoadingText>
          <LoadingText isLoading loadingWidth="90%" css={{ marginBottom: 'tighter' }}>
            Loading
          </LoadingText>
        </MediaObject>
        <Divider css={{ marginBottom: 'tight' }} />
        <LoadingText isLoading loadingWidth="80%" css={{ marginBottom: 'tight' }}>
          Loading
        </LoadingText>
        <LoadingButton isLoading loadingWidth="100%">
          Loading
        </LoadingButton>
      </>
    );
  }

  return (
    <>
      {
        <>
          {(!ouid || infoForUserQuery.isError || renterSignedInUserQuery.isError) && (
            <NoAccessLeaseAlert css={{ marginBottom: 'tight' }} />
          )}
          {isUserEmailMismatched && infoForUserQuery?.data?.email && renterSignedInUserQuery?.data?.email && (
            <Alert
              tone="warning"
              css={{ marginBottom: 'tight' }}
              actionButton={
                <TextButton asChild>
                  <a
                    href={`/user/acct/login/?url=%2Frenter-hub%2Fleasing%2Ftenant%2Fwelcome${encodeURIComponent(
                      `?${queryParameters.toString()}`,
                    )}`}
                  >
                    Switch accounts
                  </a>
                </TextButton>
              }
            >
              You’re signed in as {renterSignedInUserQuery?.data?.email}. This email isn’t connected to this lease. Sign
              in with the email address linked to your lease.
            </Alert>
          )}
          <Heading
            level={2}
            css={{
              textStyle:
                'responsive.heading-xs' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              marginBottom: 'tighter',
            }}
          >
            {leaseFromOuidQuery?.data?.leaseType === LeaseTypes.SIGNED_SELF_UPLOAD
              ? 'Your lease is ready to view'
              : 'Your lease is ready to sign'}
          </Heading>
          <Paragraph className={FULLSTORY_EXCLUDE} css={{ wordBreak: 'break-word' }}>
            Hi{infoForUserQuery?.data?.firstName != null ? ` ${infoForUserQuery?.data?.firstName}` : ''},
          </Paragraph>
          <Paragraph css={{ marginBottom: 'tight' }}>
            {leaseFromOuidQuery?.data?.leaseType === LeaseTypes.SIGNED_SELF_UPLOAD
              ? 'We’re so glad you’re here. Together with your landlord, we’ll help you review your lease.'
              : 'We’re so glad you’re here. Together with your landlord, we’ll help you review and sign your lease.'}
          </Paragraph>
          {leaseFromOuidQuery?.data?.leaseType !== LeaseTypes.SIGNED_SELF_UPLOAD && (
            <Card tone="soft" elevated={false} css={{ marginBottom: 'tight' }}>
              <Heading
                level={3}
                css={{
                  textStyle:
                    'responsive.heading-xs' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
                }}
              >
                How online leases work
              </Heading>
              <List.Root>
                <List.Item>Sign in or create an account to view your lease</List.Item>
                <List.Item>
                  Review the lease, and ask your landlord to change anything that doesn’t seem right
                </List.Item>
                <List.Item>
                  Your landlord makes any needed revisions and arranges for the lease to be signed
                  {leaseFromOuidQuery?.data?.isSelfUpload ? ' online.' : ' either online or on paper.'}{' '}
                  <Text css={{ fontWeight: 'bold' }}>
                    Then you’ll be one step closer to moving in to your new home.
                  </Text>
                </List.Item>
              </List.Root>
            </Card>
          )}
          {ouid && infoForUserQuery?.data?.email ? (
            <IdentityContentBox
              firstName={infoForUserQuery?.data?.firstName || ''}
              lastName={infoForUserQuery?.data?.lastName || ''}
              email={infoForUserQuery?.data?.email || ''}
            >
              {renderIdentityContentBoxChildren()}
            </IdentityContentBox>
          ) : (
            renderIdentityContentBoxChildren()
          )}
        </>
      }
    </>
  );
}
